import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import { BackArrow } from "../Icons/BackArrow";
import CloseIcon from "../Icons/CloseIcon";
import s from "./Modal.module.scss";

interface Props {
  children?: React.ReactNode;
  isOpened: boolean;
  onClose: () => void;
  title?: string;
  small?: boolean;
  index?: number;
  isScrollable?: boolean;
  blockStyles?: React.CSSProperties;
  headerStyles?: React.CSSProperties;
  isBackButtonVisible?: boolean;
  backAction?: () => void;
  contentNoPadding?: boolean;
}

const Modal: React.FC<Props> = observer(
  ({
    children,
    isOpened,
    onClose,
    title,
    small,
    index,
    isScrollable = true,
    blockStyles,
    headerStyles,
    isBackButtonVisible = false,
    backAction,
    contentNoPadding,
  }) => {
    const [isFadeOut, setIsFadeOut] = useState(false);
    const { lock, unlock } = useBodyScrollLock();
    const [isScrolling, setIsScrolling] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    let timeout: NodeJS.Timeout | null = null;

    useEffect(() => {
      if (isOpened) {
        lock();
      } else {
        unlock();
      }
    }, [isOpened]);

    useEffect(() => {
      const handleScroll = () => {
        setIsScrolling(true);

        if (timeout) {
          clearTimeout(timeout);
        }

        timeout = setTimeout(() => {
          setIsScrolling(false);
        }, 1000);
      };

      const wrapper = wrapperRef.current;
      if (wrapper) {
        wrapper.addEventListener("scroll", handleScroll);
      }

      return () => {
        if (wrapper) {
          wrapper.removeEventListener("scroll", handleScroll);
        }
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }, [isOpened]);

    const closeWithDelay = () => {
      setIsFadeOut(true);
      setTimeout(() => {
        onClose();
        setIsFadeOut(false);
      }, 300);
    };

    return (
      isOpened && (
        <div className={s.wrapper} style={{ zIndex: index }}>
          <div
            className={clsx(s.bg, isFadeOut && s.fadeOutBg)}
            onClick={closeWithDelay}
          ></div>
          <div
            className={clsx(
              s.block,
              isFadeOut && s.fadeOutModal,
              small && s.small,
              contentNoPadding && s.nopadding,
              isScrolling && s.scroll_active,
              !isScrollable && s.not_scrollable
            )}
            style={blockStyles}
          >
            <div className={clsx(s.top, !title && s.fe)} style={headerStyles}>
              <div className={s.back_wrapper}>
                {isBackButtonVisible && (
                  <div className={s.back} onClick={backAction}>
                    <BackArrow />
                  </div>
                )}
                {title && <div className={s.title}>{title}</div>}
              </div>
              <div className={s.close} onClick={closeWithDelay}>
                <CloseIcon />
              </div>
            </div>
            <div
              className={clsx(s.content, !isScrollable && s.not_scrollable)}
              ref={wrapperRef}
            >
              <div
                className={clsx(
                  s.content_inner,
                  contentNoPadding && s.nopadding
                )}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
      )
    );
  }
);

export default Modal;
